import styled from "styled-components";
import {
  Button,
  Modal,
  SelectInput,
  Switch
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { FeeManagerConfig } from "@dealer-center/ddc-pages-fee-manager-interface";

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;

  /**
   * MUI Override: Style Interstate TextInput labels to match design
   * Reason: Interstate TextInput does not expose variant or style props for labels.
   */
  .MuiFormLabel-root.MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 48px;
  margin-bottom: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 24px;
  }
`;

const SwitchRow = styled.div`
  margin-bottom: 16px;

  /**
   * MUI Override: Match Switch label styling to TextInput label styling.
   * Reason: Interstate Switch Label renders the same MuiInputLabel but doesn't
   * expose font/weight/transform props directly.
   */
  .MuiFormLabel-root.MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
  }
`;

const StyledModal = styled(Modal)`
  .ids-modal-title {
    margin-bottom: 16px;
    padding: 24px 24px 0 24px !important;
  }

  @media (max-width: 768px) {
    .ids-modal-title {
      padding: 16px 16px 0 16px !important;
    }

    .ids-modal-content {
      padding: 16px !important;
    }
  }

  @media (max-width: 480px) {
    .ids-modal-title {
      padding: 12px 12px 0 12px !important;
    }

    .ids-modal-content {
      padding: 12px !important;
    }
  }
`;

export interface ConfigurationModalProps {
  config: FeeManagerConfig;
  isConfigDirty: boolean;
  isOpen: boolean;
  isSaving: boolean;
  onChange: (patch: Partial<FeeManagerConfig>) => void;
  onClose: () => void;
  onSave: () => void;
}

export const ConfigurationModal = ({
  config,
  isConfigDirty,
  isOpen,
  isSaving,
  onChange,
  onClose,
  onSave
}: ConfigurationModalProps) => {
  const handleHide = ((...args: unknown[]) => {
    const reason = args[1] as string | undefined;
    if (reason === "backdropClick") return;
    onClose();
  }) as () => void;

  return (
    <StyledModal
      header={String(Locale.getText("CONFIGURATION"))}
      show={isOpen}
      size="large"
      onHide={handleHide}
    >
      <SwitchRow>
        <Switch
          checked={config.applyOnInventoryItemSave}
          label={String(Locale.getText("APPLY_ON_INVENTORY_ITEM_SAVE"))}
          labelAlignment="left"
          labelSx={{ justifyContent: "space-between", width: "100%" }}
          value={config.applyOnInventoryItemSave}
          onClick={e =>
            onChange({ applyOnInventoryItemSave: e.target.value === "checked" })
          }
        />
      </SwitchRow>

      <SwitchRow>
        <Switch
          checked={config.emitItemizedFees}
          label={String(Locale.getText("EMIT_ITEMIZED_FEES"))}
          labelAlignment="left"
          labelSx={{ justifyContent: "space-between", width: "100%" }}
          value={config.emitItemizedFees}
          onClick={e =>
            onChange({ emitItemizedFees: e.target.value === "checked" })
          }
        />
      </SwitchRow>

      <SwitchRow>
        <Switch
          checked={config.emitPriceAfterFees}
          label={String(Locale.getText("EMIT_PRICE_AFTER_FEES"))}
          labelAlignment="left"
          labelSx={{ justifyContent: "space-between", width: "100%" }}
          value={config.emitPriceAfterFees}
          onClick={e =>
            onChange({ emitPriceAfterFees: e.target.value === "checked" })
          }
        />
      </SwitchRow>

      <SwitchRow>
        <Switch
          checked={config.emitTotalFees}
          label={String(Locale.getText("EMIT_TOTAL_FEES"))}
          labelAlignment="left"
          labelSx={{ justifyContent: "space-between", width: "100%" }}
          value={config.emitTotalFees}
          onClick={e =>
            onChange({ emitTotalFees: e.target.value === "checked" })
          }
        />
      </SwitchRow>

      <FormRow>
        <SelectInput
          displayLabel
          disabled={!config.emitPriceAfterFees}
          id="priceAfterFeesInputAttribute"
          label={String(Locale.getText("PRICE_AFTER_FEES_INPUT_ATTRIBUTE"))}
          name="priceAfterFeesInputAttribute"
          options={[
            {
              label: String(Locale.getText("ASKING_PRICE")),
              value: "askingPrice"
            },
            {
              label: String(Locale.getText("INTERNET_PRICE")),
              value: "internetPrice"
            },
            {
              label: String(Locale.getText("INVOICE_PRICE")),
              value: "invoicePrice"
            },
            { label: String(Locale.getText("MSRP")), value: "msrp" },
            {
              label: String(Locale.getText("RETAIL_VALUE")),
              value: "retailValue"
            },
            { label: String(Locale.getText("SALE_PRICE")), value: "salePrice" },
            {
              label: String(Locale.getText("WHOLESALE_PRICE")),
              value: "wholesalePrice"
            }
          ]}
          value={config.priceAfterFeesInputAttribute}
          onChange={e =>
            onChange({
              priceAfterFeesInputAttribute: e.target
                .value as FeeManagerConfig["priceAfterFeesInputAttribute"]
            })
          }
        />
      </FormRow>

      <ButtonGroup>
        <Button
          analyticsConfiguration={{
            eventName: "Fee Manager Configuration close button clicked"
          }}
          buttonStyle="secondary"
          disabled={isSaving}
          onClick={onClose}
        >
          {String(Locale.getText("CLOSE"))}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "Fee Manager Configuration save button clicked"
          }}
          buttonStyle="primary"
          disabled={isSaving || !isConfigDirty}
          isLoading={isSaving}
          onClick={onSave}
        >
          {String(Locale.getText("FEEMANAGER_SAVE"))}
        </Button>
      </ButtonGroup>
    </StyledModal>
  );
};
